import { Controller } from "@hotwired/stimulus"
import { TabulatorFull as Tabulator } from "tabulator-tables"
import "tabulator-tables/dist/css/tabulator_simple.min.css"

export default class extends Controller {
  static values = {
    data: Array,
    columns: Array,
    initialSort: { type: Array, default: [] },
    initialHeaderFilter: { type: Array, default: [] },
    pageSize: { type: Number, default: 25 },
    placeholder: { type: String, default: "No data." },
    placeholderHeaderFilter: { type: String, default: "No results with the selected filters." },
  }

  connect() {
    this.table = new Tabulator(this.element, {
      data: this.dataValue,
      columns: this.columnsValue.map((col) => this.buildColumn(col)),
      layout: "fitColumns",
      pagination: true,
      paginationSize: this.pageSizeValue,
      paginationSizeSelector: [25, 50, 100],
      initialSort: this.initialSortValue,
      initialHeaderFilter: this.initialHeaderFilterValue,
      headerFilterLiveFilterDelay: 300,
      placeholder: this.placeholderValue,
      placeholderHeaderFilter: this.placeholderHeaderFilterValue,
    })
  }

  disconnect() {
    if (this.table) {
      this.table.destroy()
      this.table = null
    }
  }

  buildColumn(col) {
    const column = { ...col }

    if (column.headerFilterFunc === "excludable") {
      column.headerFilterFunc = (headerValue, rowValue) => {
        if (!headerValue) return true
        if (headerValue.startsWith("_hide:")) {
          const hidden = headerValue.slice("_hide:".length).split(",")
          return !hidden.includes(rowValue)
        }
        return rowValue === headerValue
      }
    }

    if (column.formatterType === "link") {
      delete column.formatterType
      const urlTemplate = column.urlTemplate
      delete column.urlTemplate
      column.formatter = (cell) => {
        const row = cell.getRow().getData()
        const url = urlTemplate.replace(":id", row.id)
        const val = cell.getValue()
        return `<a href="${url}" class="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)]">${this.escapeHtml(val)}</a>`
      }
    } else if (column.formatterType === "roles") {
      delete column.formatterType
      column.formatter = (cell) => {
        const roles = cell.getValue()
        if (!roles || roles.length === 0) return ""
        return roles
          .map((r) => {
            const color = r === "site_owner" ? "red" : "blue"
            return `<span class="pill ${color}">${this.escapeHtml(r)}</span>`
          })
          .join(" ")
      }
      column.headerFilter = "list"
      column.headerFilterParams = {
        values: { "": "All", admin: "Admin", site_owner: "Site owner", none: "No roles" },
      }
      column.headerFilterFunc = (headerValue, rowValue) => {
        if (!headerValue) return true
        if (headerValue === "none") return !rowValue || rowValue.length === 0
        return rowValue && rowValue.includes(headerValue)
      }
    } else if (column.formatterType === "visitorLink") {
      delete column.formatterType
      const urlTemplate = column.urlTemplate
      delete column.urlTemplate
      column.formatter = (cell) => {
        const token = cell.getValue()
        if (token == null) return `<span class="text-[var(--color-base-subtle)]">unknown</span>`
        const url = urlTemplate.replace(":token", encodeURIComponent(token))
        const short = String(token).slice(0, 8)
        return `<a href="${url}" class="text-[var(--color-primary)] hover:underline">anon-${this.escapeHtml(short)}</a>`
      }
    } else if (column.formatterType === "userLink") {
      delete column.formatterType
      const urlTemplate = column.urlTemplate
      const visitorUrlTemplate = column.visitorUrlTemplate
      delete column.urlTemplate
      delete column.visitorUrlTemplate
      column.formatter = (cell) => {
        const val = cell.getValue()
        if (val != null) {
          const url = urlTemplate.replace(":id", val)
          return `<a href="${url}" class="text-[var(--color-primary)] hover:underline">#${this.escapeHtml(val)}</a>`
        }
        const row = cell.getRow().getData()
        const token = row.visitor_token
        if (token && visitorUrlTemplate) {
          const url = visitorUrlTemplate.replace(":token", encodeURIComponent(token))
          const short = String(token).slice(0, 8)
          return `<a href="${url}" class="text-[var(--color-primary)] hover:underline">anon-${this.escapeHtml(short)}</a>`
        }
        return `<span class="text-[var(--color-base-subtle)]">anon</span>`
      }
    } else if (column.formatterType === "relativeTime") {
      delete column.formatterType
      column.formatter = (cell) => {
        const val = cell.getValue()
        if (!val) return `<span class="text-[var(--color-base-subtle)]">Never</span>`
        return this.timeAgo(new Date(val))
      }
      column.sorter = "string"
    } else if (column.formatterType === "date") {
      delete column.formatterType
      column.formatter = (cell) => {
        const val = cell.getValue()
        if (!val) return ""
        const d = new Date(val)
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      }
      column.sorter = "string"
    }

    return column
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  timeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000)
    const intervals = [
      [31536000, "year"],
      [2592000, "month"],
      [86400, "day"],
      [3600, "hour"],
      [60, "minute"],
    ]

    for (const [secs, label] of intervals) {
      const count = Math.floor(seconds / secs)
      if (count >= 1) {
        const title = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
        return `<span title="${title}">about ${count} ${label}${count > 1 ? "s" : ""} ago</span>`
      }
    }

    return "just now"
  }
}
